import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { GestureResponderEvent } from 'react-native';

import { HttpErrorCodes } from 'remote/constants';
import { useAuthContext } from 'state/auth';
import { getStoredOtpCredentials } from 'state/auth/hooks/use-account-authentication';

interface IUseSendNewOtpProps {
  setResendOrSendNewOtpError: (formError: string | null) => void;
  setOtpValidationError: (formError: string | null) => void;
  setOtpAuthError(error: null | string): void;
  setShowHasSentNewOtpMessage: (showHasSentNewOtpMessage: boolean) => void;
}

export const useSendNewOtp = ({
  setResendOrSendNewOtpError,
  setOtpValidationError,
  setOtpAuthError,
  setShowHasSentNewOtpMessage,
}: IUseSendNewOtpProps) => {
  // use this `loadingState` rather than `loading` from `seAuthContext` so that I can fake the async request within storybook easily
  const [isLoading, setIsLoading] = useState(false);

  const { formatMessage } = useIntl();
  const { signIn } = useAuthContext();
  const { email, phoneNumber } = getStoredOtpCredentials() || {};

  const sendNewOtp = useCallback(
    async (ev: GestureResponderEvent) => {
      ev.preventDefault();
      setIsLoading(true);
      setOtpValidationError(null);
      setOtpAuthError(null);

      try {
        await signIn({
          email,
          phoneNumber,
          navigateOnSuccess: false,
        });

        setShowHasSentNewOtpMessage(true);
      } catch (error) {
        // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
        switch (error?.graphQLErrors?.[0]?.extensions?.statusCode) {
          case HttpErrorCodes.TooManyRequests:
            setResendOrSendNewOtpError(
              `${formatMessage({ id: 'maxAttemptsReached' })} ${formatMessage({
                id: 'pleaseTryAgainLater',
              })}`
            );
            break;
          default:
            setResendOrSendNewOtpError(formatMessage({ id: 'authError' }));
            break;
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      setShowHasSentNewOtpMessage,
      setOtpValidationError,
      setOtpAuthError,
      signIn,
      email,
      phoneNumber,
      setResendOrSendNewOtpError,
      formatMessage,
    ]
  );

  return {
    sendNewOtp,
    isLoading,
  };
};
